import {
  Button,
  Checkbox,
  Divider,
  Grid,
  Input,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { DisplayItem, colors } from 'design-system';
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'react-feather';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import SearchBar from '../SearchBar';
import {
  filteredOptionList,
  getItemSize,
  getListHeight,
  getQueryString,
  getRenderValue,
  isCheckAll,
} from './dropdownCheckboxHelpers';

const useStyles = makeStyles({
  customCheckbox: {
    color: colors.bright_turquoise,
    padding: '0px 9px',
    '&:hover': {
      backgroundColor: 'rgba(0, 194, 255, 0.04)',
    },
    '&.Mui-checked': {
      color: colors.bright_turquoise,
      '&:hover': {
        backgroundColor: 'rgba(0, 194, 255, 0.04)',
      },
    },
  },
  itemText: {
    color: colors.madison,
    fontWeight: 400,
    marginRight: 9,
    fontSize: 15,
  },
  customMenu: {
    maxHeight: 400,
    '&::-webkit-scrollbar': {
      width: 10,
      background: '#F9F9F9',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#F9F9F9',
      boxShadow: 'inset 0 0 4px #EAEAEA',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 6,
      border: `3px solid #F9F9F9`,
      backgroundColor: '#C4C4C4',
    },
  },
  menuList: {
    paddingBottom: 0,
    paddingTop: 0,
  },
  menuItems: {
    padding: 0,
    minHeight: 0,
    paddingLeft: 9,
    whiteSpace: 'normal',
    '&.Mui-selected': {
      backgroundColor: colors.white,
    },
  },
  buttonContainer: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: colors.white,
    padding: 6,
    paddingRight: 12,
  },
  customButtonCancel: {
    fontWeight: 'bold',
    color: colors.abbey,
  },
  customButtonApply: {
    fontWeight: 'bold',
    color: colors.bright_turquoise,
    '&:disabled': {
      color: colors.silver,
    },
  },
  inputText: {
    color: colors.madison,
    marginBottom: 6,
  },
  inputUnderline: {
    borderBottom: 'none',
  },
  searchIcon: {
    marginBottom: 12,
  },
  customSelect: {
    padding: '12px 16px',
    color: colors.madison,
    '&.MuiSelect-select': {
      paddingRight: 60,
      fontWeight: 400,
    },
    '&:hover': {
      backgroundColor: colors.cloud,
    },
    '&:focus': {
      backgroundColor: colors.cloud,
    },
  },
  focusedInput: {
    backgroundColor: colors.cloud,
  },
  customDropdown: {
    borderRadius: 10,
  },
  titleText: {
    color: colors.abbey,
    fontSize: 13,
    fontWeight: 500,
  },
  divider: {
    backgroundColor: colors.gallery,
    height: 1,
  },
});

type Props = {
  // Automatization Id
  id: string;
  optionList: DisplayItem[];
  applyChanges: (selected: string, renderValue: string) => void;
  currentValues: string[];
  element?: string;
  showDetails?: boolean;
  titleText?: string;
};

const DropdownCheckbox = ({
  id,
  optionList,
  applyChanges,
  currentValues,
  element = 'Licencias',
  showDetails,
  titleText,
}: Props): JSX.Element => {
  const classes = useStyles();
  const [selected, setSelected] = useState<string[]>(currentValues);
  const [search, setSearch] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);

  const listRef = React.useRef<VariableSizeList<DisplayItem[]>>(null);
  const dropdownList = useMemo(() => {
    const list = filteredOptionList(optionList, search);
    // Reset list each time it changes
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
    return list;
  }, [optionList, search, listRef]);

  const isAllSelected = useMemo(
    () => optionList.length === selected.length,
    [optionList, selected],
  );

  const handleClick = (event: React.MouseEvent<HTMLLIElement>) => {
    const currentValue = event.currentTarget.getAttribute('value') as string;

    if (selected.indexOf(currentValue) > -1) {
      setSelected(selected.filter((item) => item !== currentValue));
      return;
    }

    if (currentValue === 'TODAS') {
      setSelected(isAllSelected ? [] : optionList.map((item) => item.Value));
      return;
    }
    setSelected([...selected, currentValue]);
  };

  useEffect(() => {
    setSelected(currentValues);
  }, [currentValues]);

  const childItems = ({ style, index, data }: ListChildComponentProps) => {
    const item = data[index];

    return (
      <MenuItem
        id={`${id}-item-${index}`}
        data-testid={item.Value}
        key={item.Value}
        value={item.Value}
        classes={{
          root: classes.menuItems,
        }}
        style={style}
        onClick={handleClick}
      >
        <Checkbox
          checked={
            isCheckAll(item.Value)
              ? isAllSelected
              : selected.indexOf(item.Value) > -1
          }
          classes={{
            root: classes.customCheckbox,
          }}
          indeterminate={
            isCheckAll(item.Value)
              ? !isAllSelected && selected.length > 0
              : false
          }
        />
        <ListItemText
          primary={item.Text}
          classes={{
            primary: classes.itemText,
          }}
        />
      </MenuItem>
    );
  };

  return (
    <>
      {showDetails && (
        <Typography className={classes.titleText} data-testid="dropdown-title">
          {titleText}
        </Typography>
      )}
      <Select
        id={id}
        data-testid="multiple-dropdown"
        multiple
        value={currentValues}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        renderValue={(item) => getRenderValue(item as string[], -1, element)}
        MenuProps={{
          classes: {
            list: classes.menuList,
            paper: classes.customDropdown,
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
          onClose: (_e, reason) => {
            if (reason === 'backdropClick') {
              setOpen(false);
            }
          },
        }}
        displayEmpty
        IconComponent={({ className }) => (
          <ChevronDown
            className={className}
            color={colors.madison}
            style={{ right: 10 }}
          />
        )}
        classes={{
          root: classes.customSelect,
        }}
        input={
          <Input
            disableUnderline
            classes={{ focused: classes.focusedInput }}
            style={{ height: 'inherit' }}
          />
        }
      >
        <SearchBar
          id={`${id}-input-searchbar`}
          data-testid="search-bar"
          search={search}
          setSearch={setSearch}
        />
        <VariableSizeList
          ref={listRef}
          itemSize={(index: number) =>
            getItemSize(dropdownList[index].Text as string)
          }
          itemData={dropdownList}
          itemCount={dropdownList.length}
          width="100%"
          height={getListHeight(dropdownList)}
          className={classes.customMenu}
          overscanCount={10}
          data-testid="variable-list"
        >
          {childItems}
        </VariableSizeList>
        <Grid
          container
          className={classes.buttonContainer}
          justifyContent="flex-end"
        >
          <Button
            id={`${id}-button-cancel`}
            data-testid="cancel-button"
            className={classes.customButtonCancel}
            onClick={() => {
              setOpen(false);
              setSelected(currentValues);
            }}
          >
            Cancelar
          </Button>
          <Button
            id={`${id}-button-apply`}
            data-testid="apply-button"
            className={classes.customButtonApply}
            onClick={() => {
              setOpen(false);
              applyChanges(
                getQueryString(selected),
                getRenderValue(selected, optionList.length, element),
              );
            }}
            disabled={currentValues.join('') === selected.join('')}
          >
            Aplicar
          </Button>
        </Grid>
      </Select>
      {showDetails && (
        <Divider className={classes.divider} data-testid="selector-divider" />
      )}
    </>
  );
};

export default DropdownCheckbox;
